'use client'

import type { ComponentProps } from 'react'

import {
  cn,
  DropdownMenuContent as RepoDropdownMenuContent,
  HoverCardContent as RepoHoverCardContent,
  PopoverContent as RepoPopoverContent
} from '@repo/ui'

/**
 * The Vaults app overlay primitives — thin wrappers over `@repo/ui`'s
 * HoverCard/Popover/DropdownMenu that restore the padding and brand radius the
 * bespoke `@morpho-org/ui-v6` components used to ship, without bending the
 * shared `@repo/ui` package or touching every call site. Import those parts
 * from here instead of `@repo/ui`.
 *
 * Only the three `*Content` components are wrapped; the rest are re-exported
 * unchanged. Padding is a `p-*` default merged FIRST, so a call-site `className`
 * still wins (`cn`).
 */

// Pass-through primitives (behaviour identical to @repo/ui).
export {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  HoverCard,
  HoverCardTrigger,
  Popover,
  PopoverTrigger
} from '@repo/ui'

/**
 * `@repo/ui` HoverCardContent ships `p-4` + `rounded-md` (6px); the bespoke Vaults app
 * HoverCard.Content had **no** padding (consumers pad their own content), a 20px
 * brand radius, and `overflow-hidden` so children clip to the rounded corner.
 * Restore all three as defaults (`p-0`, `rounded-[20px]`, `overflow-hidden`).
 */
export function HoverCardContent({
  className,
  ...props
}: ComponentProps<typeof RepoHoverCardContent>) {
  return (
    <RepoHoverCardContent
      className={cn('overflow-hidden rounded-[20px] p-0', className)}
      {...props}
    />
  )
}

/**
 * `@repo/ui` PopoverContent ships `p-4` (16px) + `rounded-md` (6px) + a fixed
 * `w-72`; the bespoke Vaults app Popover.Content used `p-md` (20px), `rounded-lg` (8px),
 * `overflow-hidden`, and a responsive mobile default that narrowed the popover to
 * `70vw` and re-centred it under the trigger. Restore all of them so default-relying
 * popovers keep their brand padding/radius, corner clipping, and mobile width.
 */
export function PopoverContent({ className, ...props }: ComponentProps<typeof RepoPopoverContent>) {
  return (
    <RepoPopoverContent
      className={cn(
        'overflow-hidden rounded-lg p-md',
        'max-md:mx-[calc((var(--radix-popover-content-available-width)-70vw)/2)] max-md:w-[70vw]',
        className
      )}
      {...props}
    />
  )
}

/**
 * `@repo/ui` DropdownMenuContent ships `rounded-md` (6px) + `p-1` (4px) with
 * `rounded-sm` (4px) items. The Vaults app panels use the 20px brand radius, which leaves
 * the hovered item 12px too sharp: concentric corners want inner = outer minus
 * padding (20 - 8 = 12px), the ratio `MenuDropdown` already uses.
 */
export function DropdownMenuContent({
  className,
  ...props
}: ComponentProps<typeof RepoDropdownMenuContent>) {
  return (
    <RepoDropdownMenuContent
      className={cn(
        'border-border p-xs rounded-[20px] border-[0.5px] shadow-lg',
        '**:data-[slot=dropdown-menu-item]:rounded-xl',
        className
      )}
      {...props}
    />
  )
}
